import {
  isPlatformCurrency,
  isPlatformLocale,
  PLATFORM_CURRENCIES,
  PLATFORM_LOCALES,
  type PlatformCurrency,
  type PlatformLocale,
} from "@blockdaemon/widget-metadata";
import { useLocation } from "@tanstack/react-router";

import type { BatchMethod } from "./batch-method";
import { tryParseBatchMethod } from "./batch-method";

/**
 * Showcase / Storybook preview flag (`?embed=true`).
 *
 * Hides the multi-product showcase shell so only the product router renders.
 * Product runtime behaviour (wallet mode, layout, auth) must not depend on this;
 * use {@link isInIframe} for iframe-only behaviour (e.g. JWT auth). Wallet mode follows
 * customer config via {@link ../wallet-provider-mode.resolveWalletProviderMode}.
 */
/**
 * Whether the product widget should render the shell mesh/gradient around a centred card.
 * The demo shell paints mesh for normal showcase browsing; the widget paints it for iframes
 * and for inline `?embed=true` previews after the shell is hidden.
 */
export function shouldRenderWidgetMeshBackground(
  showcaseEmbedPreview: boolean,
  isFramed: boolean,
  isShowcase: boolean,
): boolean {
  return isFramed || showcaseEmbedPreview || !isShowcase;
}

export function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;

  const urlParams = new URLSearchParams(window.location.search);
  const embedParam = urlParams.get("embed");
  return embedParam === "true";
}

export function useEmbedMode(): boolean {
  const location = useLocation();
  const search = location.search as { embed?: boolean };
  if (search.embed === true) {
    return true;
  }

  // Child routes validate their own search slices; retain `embed` on the URL even when
  // it is absent from the active route's parsed search.
  return isEmbedMode();
}

export function getColorSchemeOverride(): "light" | "dark" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const colorSchemeParam = urlParams.get("colorScheme");
  return colorSchemeParam === "light" || colorSchemeParam === "dark" ? colorSchemeParam : null;
}

export function getWidthOverride(): "mobile" | "tablet" | "desktop" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const widthParam = urlParams.get("width");

  if (!widthParam) return null;

  if (widthParam === "mobile" || widthParam === "tablet" || widthParam === "desktop") {
    return widthParam;
  }

  const numericWidth = Number.parseInt(widthParam, 10);
  if (!Number.isNaN(numericWidth) && numericWidth >= 375 && numericWidth <= 1366) {
    return numericWidth;
  }

  return null;
}

export function getHeightOverride(): "mobile" | "tablet" | "desktop" | number | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const heightParam = urlParams.get("height");

  if (!heightParam) return null;

  if (heightParam === "mobile" || heightParam === "tablet" || heightParam === "desktop") {
    return heightParam;
  }

  const numericHeight = Number.parseInt(heightParam, 10);
  if (!Number.isNaN(numericHeight) && numericHeight >= 480 && numericHeight <= 1200) {
    return numericHeight;
  }

  return null;
}

export function getCurrencyOverride(
  allowedCurrencies: readonly PlatformCurrency[] = PLATFORM_CURRENCIES,
): PlatformCurrency | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get("currency");

  if (urlCurrency && isPlatformCurrency(urlCurrency) && allowedCurrencies.includes(urlCurrency)) {
    return urlCurrency;
  }

  // localStorage throws SecurityError in Safari private browsing and cross-origin iframes.
  let savedCurrency: string | null = null;
  try {
    savedCurrency = localStorage.getItem("widget-currency");
  } catch {
    // Storage access denied — treat as no saved preference.
  }
  if (
    savedCurrency &&
    isPlatformCurrency(savedCurrency) &&
    allowedCurrencies.includes(savedCurrency)
  ) {
    return savedCurrency;
  }

  return null;
}

export function getLocaleOverride(
  allowedLocales: readonly PlatformLocale[] = PLATFORM_LOCALES,
): PlatformLocale | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const localeParam = urlParams.get("locale");

  if (!localeParam) return null;

  if (isPlatformLocale(localeParam) && allowedLocales.includes(localeParam)) {
    return localeParam;
  }

  return null;
}

export function getWalletModeOverride(): "internal" | "external" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const walletModeParam = urlParams.get("walletMode");
  return walletModeParam === "internal" || walletModeParam === "external" ? walletModeParam : null;
}

export function getBatchModeOverride(): BatchMethod | "off" | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const batchModeParam = urlParams.get("batchMode");
  if (!batchModeParam) return null;
  if (batchModeParam.toLowerCase() === "off") return "off";
  return tryParseBatchMethod(batchModeParam);
}

export function getBatcherContractOverride(): string | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("batcherContract");
  if (!param) return null;

  return /^0x[a-fA-F0-9]{40}$/.test(param) ? param : null;
}

export function getAtomicRequiredOverride(): boolean | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("atomicRequired");

  if (param === "true") return true;
  if (param === "false") return false;

  return null;
}

export function getEnableFeeOverride(): boolean | null {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const param = urlParams.get("enableFee");

  if (param === "true") return true;
  if (param === "false") return false;

  return null;
}

/**
 * True for standalone product widget bundles (earn, tokenization).
 * False when the product router is compiled into the multi-product showcase host.
 *
 * Set at compile time via Vite `define` when `createWidgetViteConfig({ showcaseHost: true })`
 * is used (`apps/widget-showcase` only). Product apps omit that flag; do not use `.env`.
 */
export function isShowcaseHost(): boolean {
  return import.meta.env.VITE_WIDGET_SHOWCASE === "true";
}

export function isProductWidgetBuild(): boolean {
  return !isShowcaseHost();
}

/** True when the widget document is running inside a parent frame (customer embed). */
export function isInIframe(): boolean {
  if (typeof window === "undefined") return false;

  return window !== window.top;
}
